/**
 * Measurement geometry for the Instrument direction: the numbers the evidence panels print.
 *
 * The server says which spots pair up; it does not say how well they sit on top of one another.
 * `similarityFit` answers that for display: a least-squares similarity transform (scale,
 * rotation, translation) from the query's matched spots onto the reference's, and each pair's
 * residual after it, as a share of the reference pattern's size.
 */
import type { Constellation } from "@/api/types";
import type { Bounds, Point } from "@/lib/constellation";

/** A query spot and the reference spot it is paired with, in their own image pixels. */
export interface SpotPair {
  query: Point;
  gallery: Point;
}

export interface SimilarityFit {
  /** Query pixels → reference pixels. */
  map: (p: Point) => Point;
  scale: number;
  rotationDeg: number;
  /** Per pair, in the order given, as a fraction of the reference pattern's RMS radius. */
  residuals: number[];
  rms: number;
  median: number;
}

function mean(points: readonly Point[]): Point {
  let x = 0;
  let y = 0;
  for (const p of points) {
    x += p.x;
    y += p.y;
  }
  return { x: x / points.length, y: y / points.length };
}

function medianOf(values: readonly number[]): number {
  const sorted = values.toSorted((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/**
 * Fit `reference ≈ z · query + t` with z complex (Umeyama without reflection). Null for fewer
 * than two pairs or a degenerate (single-point) query pattern: there is nothing to fit.
 */
export function similarityFit(pairs: readonly SpotPair[]): SimilarityFit | null {
  if (pairs.length < 2) return null;
  const qc = mean(pairs.map((p) => p.query));
  const gc = mean(pairs.map((p) => p.gallery));
  let re = 0;
  let im = 0;
  let qq = 0;
  let gg = 0;
  for (const { query, gallery } of pairs) {
    const ax = query.x - qc.x;
    const ay = query.y - qc.y;
    const bx = gallery.x - gc.x;
    const by = gallery.y - gc.y;
    re += ax * bx + ay * by;
    im += ax * by - ay * bx;
    qq += ax * ax + ay * ay;
    gg += bx * bx + by * by;
  }
  if (qq === 0 || gg === 0) return null;
  const zr = re / qq;
  const zi = im / qq;
  const map = (p: Point): Point => {
    const ax = p.x - qc.x;
    const ay = p.y - qc.y;
    return { x: zr * ax - zi * ay + gc.x, y: zr * ay + zi * ax + gc.y };
  };
  const radius = Math.sqrt(gg / pairs.length);
  const residuals = pairs.map(({ query, gallery }) => {
    const m = map(query);
    return Math.hypot(m.x - gallery.x, m.y - gallery.y) / radius;
  });
  const rms = Math.sqrt(residuals.reduce((s, r) => s + r * r, 0) / residuals.length);
  return {
    map,
    scale: Math.hypot(zr, zi),
    rotationDeg: (Math.atan2(zi, zr) * 180) / Math.PI,
    residuals,
    rms,
    median: medianOf(residuals),
  };
}

export interface Triangle {
  vertices: [number, number, number];
  /** Groth (1986) invariants: longest / shortest side, and the cosine of the angle between. */
  ratio: number;
  cosine: number;
}

/**
 * A representative Groth triangle: the largest spot nearest the pattern's centre and its two
 * nearest neighbours, with the invariants the matcher compares. Null below three spots.
 */
export function grothTriangle(c: Constellation): Triangle | null {
  const pts = c.spots.map(([x, y]) => ({ x, y }));
  if (pts.length < 3) return null;
  const centre = mean(pts);
  const spread = Math.max(...pts.map((p) => Math.hypot(p.x - centre.x, p.y - centre.y)), 1);
  let anchor = 0;
  let best = -Infinity;
  c.spots.forEach(([x, y, r], i) => {
    const weight = r - (3 * Math.hypot(x - centre.x, y - centre.y)) / spread;
    if (weight > best) {
      best = weight;
      anchor = i;
    }
  });
  const a = pts[anchor]!;
  const near = pts
    .map((p, i) => ({ i, d: Math.hypot(p.x - a.x, p.y - a.y) }))
    .filter(({ i }) => i !== anchor)
    .toSorted((u, v) => u.d - v.d);
  // Nearest neighbours make an unreadably small triangle in a dense pattern; reach a little
  // further (about 3% of the spots) so the figure shows its shape.
  const k = Math.min(Math.max(Math.round(pts.length * 0.03), 1), near.length - 1);
  const b = near[k - 1]!.i;
  const cIndex = pickThird(
    pts,
    a,
    pts[b]!,
    near.slice(k).map(({ i }) => i),
  );
  const v: [number, number, number] = [anchor, b, cIndex];
  return { vertices: v, ...grothInvariants(pts[anchor]!, pts[b]!, pts[cIndex]!) };
}

/** The first candidate that makes a well-formed (not near-collinear) triangle with a and b. */
function pickThird(pts: readonly Point[], a: Point, b: Point, candidates: number[]): number {
  for (const i of candidates) {
    const p = pts[i]!;
    const cross = (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
    const base = Math.hypot(b.x - a.x, b.y - a.y) * Math.hypot(p.x - a.x, p.y - a.y);
    if (base > 0 && Math.abs(cross) / base > 0.5) return i;
  }
  return candidates[0]!;
}

/** Ratio of longest to shortest side, and cos of the angle at their shared vertex. */
export function grothInvariants(p1: Point, p2: Point, p3: Point) {
  const sides = [
    { len: Math.hypot(p2.x - p1.x, p2.y - p1.y), from: p1, to: p2 },
    { len: Math.hypot(p3.x - p2.x, p3.y - p2.y), from: p2, to: p3 },
    { len: Math.hypot(p1.x - p3.x, p1.y - p3.y), from: p3, to: p1 },
  ].toSorted((s, t) => s.len - t.len);
  const shortest = sides[0]!;
  const longest = sides[2]!;
  if (shortest.len === 0) return { ratio: Infinity, cosine: 1 };
  const shared = [longest.from, longest.to].find((p) => p === shortest.from || p === shortest.to)!;
  const u = longest.from === shared ? longest.to : longest.from;
  const w = shortest.from === shared ? shortest.to : shortest.from;
  const dot = (u.x - shared.x) * (w.x - shared.x) + (u.y - shared.y) * (w.y - shared.y);
  return { ratio: longest.len / shortest.len, cosine: dot / (longest.len * shortest.len) };
}

/** Bounds of all spots (not the body outline), or the whole frame when there are none. */
export function spotBounds(c: Constellation): Bounds {
  if (c.spots.length === 0) return { x: 0, y: 0, width: c.width, height: c.height };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of c.spots) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return { x: minX, y: minY, width: Math.max(maxX - minX, 1), height: Math.max(maxY - minY, 1) };
}
